import { useRef, useState, type DragEvent } from 'react';
import React from 'react';
import { cx } from '../../../utils/cx';
import { formatFileSize } from '../../../utils/formatFileSize';
import { Button } from '../../../components/ui/Button/Button';
import { Icon } from '../../../components/ui/Icon/Icon';
import type { AttachedFile } from '../../../types/support';
import styles from './FileDropzone.module.scss';

interface FileDropzoneProps {
  files: AttachedFile[];
  onAdd: (files: FileList | File[]) => void;
  onRemove: (id: string) => void;
}

export function FileDropzone({ files, onAdd, onRemove }: FileDropzoneProps) {
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragging(false);
    if (e.dataTransfer.files.length) onAdd(e.dataTransfer.files);
  };

  return (
    <>
      <div
        className={cx(styles.dropzone, dragging && styles.drag)}
        onDragOver={(e) => {
          e.preventDefault();
          setDragging(true);
        }}
        onDragLeave={(e) => {
          if (!e.currentTarget.contains(e.relatedTarget as Node)) setDragging(false);
        }}
        onDrop={handleDrop}
      >
        <div className={styles.icon}>
          <Icon name="upload" size={20} strokeWidth={1.7} />
        </div>
        <b>Перетащите файлы сюда</b>
        <p>Скриншот с ошибкой сокращает время разбора в разы — на нём видно и экран, и данные</p>
        <div className={styles.formats}>PNG, JPG, PDF, XLSX, DOCX, LOG · до 20 МБ на файл</div>
        <div className={styles.actions}>
          <Button variant="upload" onClick={() => inputRef.current?.click()}>
            Выбрать файлы
          </Button>
        </div>
        <input
          ref={inputRef}
          type="file"
          multiple
          hidden
          onChange={(e) => {
            if (e.target.files?.length) onAdd(e.target.files);
            e.target.value = '';
          }}
        />
      </div>

      {files.length > 0 && (
        <ul className={styles.list}>
          {files.map(({ id, file }) => (
            <li key={id} className={styles.file}>
              <span className={styles.fileIcon}>
                <Icon name="file" size={13} />
              </span>
              <span className={styles.fileCopy}>
                <b>{file.name}</b>
                <small>{formatFileSize(file.size)}</small>
              </span>
              <button
                type="button"
                className={styles.remove}
                aria-label={`Убрать файл ${file.name}`}
                onClick={() => onRemove(id)}
              >
                <Icon name="close" size={13} strokeWidth={1.9} />
              </button>
            </li>
          ))}
        </ul>
      )}
    </>
  );
}
